import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { ClientRegistrationModal } from '@/components/order/ClientRegistrationModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useClientSearch } from '@/hooks/useClientSearch'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import { 
  Search, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  UserPlus,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import type { ClientProfile } from '@/services/orderApi'
import type { SubmitRegistrationResponse } from '@/types/registration'

export function ClientSelectionPage() {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { setClient, setCurrentStep } = useOrderCreationStore()
  const { clients, isLoading, error, searchTerm, search, clearSearch } = useClientSearch({
    debounceMs: 400,
    minSearchLength: 2,
  })

  const [inputValue, setInputValue] = useState('')
  const [showRegistrationModal, setShowRegistrationModal] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    search(value)
  }

  const handleSelectClient = useCallback(
    (client: ClientProfile) => {
      setClient(
        client.id,
        client.fullName || client.displayName || '',
        client.phone,
        client.email
      )
      setCurrentStep(2)
      navigate('/create-order/category')
    },
    [setClient, setCurrentStep, navigate]
  )

  const handleClear = () => {
    setInputValue('')
    clearSearch()
  }

  const handleRegistrationSuccess = useCallback((result: SubmitRegistrationResponse) => {
    // After successful registration, search for the new client by phone
    setInputValue(result.user.phone_number)
    search(result.user.phone_number)
    setShowRegistrationModal(false)
  }, [search])

  const formatAge = (age?: number, dateOfBirth?: string) => {
    if (age) return `${age} ${t('orderCreation.years')}`
    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth)
      const today = new Date()
      const calculatedAge = today.getFullYear() - birthDate.getFullYear()
      return `${calculatedAge} ${t('orderCreation.years')}`
    }
    return null
  }

  return (
    <OrderCreationLayout
      titleKey="orderCreation.selectClient"
      descriptionKey="orderCreation.selectClientDescription"
      step={1}
      showBack={false}
      showCancel={true}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Search Box with Add Button */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  {t('orderCreation.searchForClient')}
                </CardTitle>
                <CardDescription className="mt-1">
                  {t('orderCreation.searchForClientDescription')}
                </CardDescription>
              </div>
              <Button
                onClick={() => setShowRegistrationModal(true)}
                className="gap-2 flex-shrink-0 w-full sm:w-auto"
                size="sm"
              >
                <UserPlus className="h-4 w-4" />
                {t('orderCreation.addNewClient')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={t('orderCreation.searchPlaceholder')}
                className="ps-10 pe-24 text-lg h-12"
                autoFocus
              />
              <div className="absolute end-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {isLoading && (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                )}
                {inputValue && !isLoading && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="h-8 px-2"
                  >
                    {t('common.clearFilters')}
                  </Button>
                )}
              </div>
            </div>

            {/* Search Tips */}
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">
                <Phone className="h-3 w-3 me-1" />
                {t('orderCreation.searchTipPhone')}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <User className="h-3 w-3 me-1" />
                {t('orderCreation.searchTipName')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && searchTerm.length >= 2 && (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-1/3" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Results */}
        {!isLoading && clients.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('orderCreation.clientsFound', { count: clients.length })}
            </p>
            {clients.map((client) => (
              <Card
                key={client.id}
                className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all"
                onClick={() => handleSelectClient(client)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start sm:items-center justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
                      <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                      </div>
                      
                      <div className="space-y-1 min-w-0">
                        <p className="font-semibold text-foreground truncate">
                          {client.fullName || client.displayName}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                          {client.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3 flex-shrink-0" />
                              {client.phone}
                            </span>
                          )}
                          {client.email && (
                            <span className="hidden sm:flex items-center gap-1">
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{client.email}</span>
                            </span>
                          )}
                          {formatAge(client.age, client.dateOfBirth) && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 flex-shrink-0" />
                              {formatAge(client.age, client.dateOfBirth)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <Button variant="outline" size="sm" className="flex-shrink-0">
                      {t('orderCreation.select')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results */}
        {!isLoading && searchTerm.length >= 2 && clients.length === 0 && !error && (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                {t('orderCreation.noClientsFound', { term: searchTerm })}
              </p>
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setShowRegistrationModal(true)}
              >
                <UserPlus className="h-4 w-4" />
                {t('orderCreation.registerNewClient')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Initial State - Show Instructions */}
        {!isLoading && searchTerm.length < 2 && (
          <Card className="bg-muted/30">
            <CardContent className="py-8 text-center">
              <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {t('orderCreation.enterAtLeastTwoCharacters')}
              </p>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Client Registration Modal */}
      <ClientRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSuccess={handleRegistrationSuccess}
        initialPhoneNumber={inputValue.match(/^\d+$/) ? inputValue : ''}
      />
    </OrderCreationLayout>
  )
}
